import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import FormGroup from '../../components/Form/FormGroup';
import Input from '../../components/Form/Input';
import Button from '../../components/Form/Button';
import Box from '../../components/Box';
import { StyledForm } from './styles';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { DialogCancelCreateUpdate } from '../shared/DialogCancelCreateUpdate';
import { jurisprudenciaService } from '../../api/services';
import { OptionType, Select } from '../../components/Form/Select';
import Paragraph from '../../components/Text/Paragraph';
import { DialogConfirmAction } from '../shared/DialogConfirmAction';
import TextEditor from './TextEditor';
import Accordion from '../../components/Accordion';
import {
  InitialJurisprudenciaForm,
  JurisprudenciaFormValues,
  JurisprudenciaDeleteResponse,
  TextoJurisprudenciaResponse,
} from '../../types/Jurisprudencia.types';

interface TextFormProps {
  type: 'create' | 'update' | 'view' | 'duplicate';
  boxType?: 'page' | 'suspense-page';
  initialData?: InitialJurisprudenciaForm;
  onSubmit: (data: JurisprudenciaFormValues) => void;
  onCancel: () => void;
}

const validationSchema = yup.object({
  nome: yup.string().required('O campo nome é obrigatório'),
  tribunal: yup.string().required('O campo tribunal é obrigatório'),
  orgao: yup.string().required('O campo órgão é obrigatório'),
  tipoDecisao: yup.string().required('O campo tipo da decisão é obrigatório'),
  dataPublicacao: yup.string().required('O campo data de publicação é obrigatório'),
  assunto: yup.string().required('O campo assunto é obrigatório'),
  area: yup.string().required('O campo área é obrigatório'),
  valorTexto: yup.string().required('O campo valor do texto é obrigatório'),
  enderecoArquivo: yup.string().required('O campo endereço do arquivo é obrigatório'),
});

const JurisprudenciaForm = ({ type, boxType = 'page', initialData, onSubmit, onCancel }: TextFormProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [jurisprudencias, setJurisprudencias] = useState<readonly OptionType[]>([]);
  const [currentTab, setCurrentTab] = useState(1);
  const [documentos, setDocumentos] = useState<JurisprudenciaDeleteResponse[]>([]);
  const [textos, setTextos] = useState<JurisprudenciaDeleteResponse[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [textoAssociados, setTextoAssociados] = useState<TextoJurisprudenciaResponse[]>([]);

  const idJurisprudencia = initialData?.idJurisprudencia || '';

  // useEffect(() => {
  //   async function fetchDocumentos() {
  //     const result = await jurisprudenciaService.getDocumento(idJurisprudencia);
  //     if (result) {
  //       setDocumentos(result);
  //     }
  //   }

  //   fetchDocumentos();
  // }, []);

  useEffect(() => {
    async function fetchTextos() {
      try {
        const result = await jurisprudenciaService.getTexto(idJurisprudencia);
        if (result) {
          setTextoAssociados(result);
        }
      } catch (error) {
        console.error('Erro ao buscar textos:', error);
      }
    }

    if (type === 'view') {
      fetchTextos();
    }
  }, [idJurisprudencia, type]); // dependências

  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState: { errors },
  } = useForm<JurisprudenciaFormValues>({
    defaultValues: {
      ...initialData,
    },
    resolver: yupResolver(validationSchema),
  });

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  }, []);

  const setTab = useCallback(
    (tab: number) => (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      setCurrentTab(tab);
    },
    []
  );

  const navigate = useNavigate();
  const navigateToTexts = useCallback(() => {
    navigate('/jurisprudencias');
  }, []);
  const handleEditorChange = useCallback((content: string) => setValue('valorTexto', content), []);

  const handleConfirmAndSubmit = useCallback(async () => {
    try {
      if (selectedFile) {
        const response = await jurisprudenciaService.salvarArquivo(selectedFile);
        if (!response) throw new Error('Erro ao salvar arquivo');

        const fileId = response.mensagem;
        console.log(fileId + '.' + selectedFile.type.split('/')[1]);
        // Insere a URL no formulário
        setValue('enderecoArquivo', fileId + '.' + selectedFile.type.split('/')[1]);
      }

      // Dispara o submit do formulário
      document.querySelector('form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    } catch (error) {
      console.error('Erro no envio do arquivo:', error);
    }
  }, [selectedFile, setValue]);

  return (
    <>
      <StyledForm
        // eslint-disable-next-line react/jsx-no-bind
        onSubmit={handleSubmit(onSubmit)}>
        <Box
          type={boxType}
          className="box-info"
          padding={false}
          border={false}
          footer={
            type === 'view' ? (
              <Button className="cancel-button" kind="alert" onClick={onCancel}>
                Voltar
              </Button>
            ) : (
              <>
                <DialogCancelCreateUpdate type={type} navigateToListPage={navigateToTexts} />

                {type === 'update' ? (
                  <DialogConfirmAction
                    type="update"
                    trigger={<Button className="submit-button">Salvar</Button>}
                    title="Confirmar alteração?"
                    text={`Tem certeza que deseja alterar a jurisprudencia ${initialData?.nome}? Esta jurisprudencia pode estar relacionado a um ou mais modelos e sua alteração impactará em todos. Deseja continuar com a alteração?`}
                    onConfirm={handleConfirmAndSubmit}
                  />
                ) : (
                  <Button
                    className="submit-button"
                    type="submit"
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={(e) => {
                      e.preventDefault(); // Impede o reload da página
                      handleConfirmAndSubmit(); // Aqui você chama o react-hook-form
                    }}>
                    Salvar
                  </Button>
                )}
              </>
            )
          }>
          <Accordion
            content={
              currentTab === 1 ? (
                <div className="content">
                  <Box grid={5} border={false} padding={false}>
                    <FormGroup error={errors.nome?.message}>
                      <Input
                        {...register('nome')}
                        label="Nome do texto"
                        placeholder="Digite o nome do texto"
                        disabled={type === 'view'}
                      />
                    </FormGroup>

                    <FormGroup error={errors.tribunal?.message}>
                      <Input
                        {...register('tribunal')}
                        label="Tribunal"
                        placeholder="Ex: TJSP, STF, STJ"
                        disabled={type === 'view'}
                      />
                    </FormGroup>

                    <FormGroup error={errors.orgao?.message}>
                      <Input
                        {...register('orgao')}
                        label="Órgão"
                        placeholder="Ex: 20ª CC, 2ª Turma Recursal"
                        disabled={type === 'view'}
                      />
                    </FormGroup>

                    <FormGroup error={errors.tipoDecisao?.message}>
                      <Input
                        {...register('tipoDecisao')}
                        label="Tipo de decisão"
                        placeholder="Ex: sentença, acórdão de Agravo"
                        disabled={type === 'view'}
                      />
                    </FormGroup>

                    <FormGroup error={errors.dataPublicacao?.message}>
                      <Input
                        {...register('dataPublicacao')}
                        label="Ano de julgamento/publicação"
                        placeholder="Ex: 2024"
                        type="number"
                        disabled={type === 'view'}
                      />
                    </FormGroup>

                    <FormGroup error={errors.assunto?.message}>
                      <Input
                        {...register('assunto')}
                        label="Assunto"
                        placeholder="Ex: dano moral, cobrança em dobro"
                        disabled={type === 'view'}
                      />
                    </FormGroup>

                    <FormGroup error={errors.area?.message}>
                      <Input
                        {...register('area')}
                        label="Área"
                        placeholder="Ex: bancário, e-commerce"
                        disabled={type === 'view'}
                      />
                    </FormGroup>
                  </Box>
                </div>
              ) : (
                <>
                  {/* {documentos.length > 0 && (
                    <div className="info">
                      <Paragraph className="label">Documentos vinculados</Paragraph>
                      <ul>
                        {documentos.map((doc, index) => (
                          <li key={index}>{doc.mensagem}</li>
                        ))}
                      </ul>
                    </div>
                  )} */}

                  {textoAssociados.length > 0 && (
                    <div className="info">
                      <Paragraph className="label">Textos vinculados</Paragraph>
                      <ul>
                        {textoAssociados.map((texto, index) => (
                          <li key={index}>{texto.texto}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )
            }>
            <div className="tabs">
              <button className={`tab${currentTab === 1 ? ' active' : ''}`} onClick={setTab(1)}>
                DADOS JURISPRUDENCIA
              </button>
              {(type === 'view' || type === 'update') && (
                <button className={`tab${currentTab === 2 ? ' active' : ''}`} onClick={setTab(2)}>
                  VINCULOS
                </button>
              )}
            </div>
          </Accordion>
        </Box>

        <div className="content-editor">
          <TextEditor
            onEditorChange={handleEditorChange}
            initialValue={initialData?.valorTexto as string}
            disabled={type === 'view' ? true : false}
          />
        </div>
        <FormGroup error={errors.enderecoArquivo?.message}>
          <Input
            {...register('enderecoArquivo')}
            label="Anexar arquivo"
            disabled={type === 'view'}
            type="file"
            onChange={handleFileChange}
            // className="upload-files"
          />
          {/* <div className="upload-files">
            <label htmlFor="file-upload" className="upload-label">
              Anexar arquivo
            </label>
            <input id="file-upload" type="file" disabled={type === 'view'} onChange={handleFileChange} />
            {selectedFile && <span className="file-name">{selectedFile.name}</span>}
          </div> */}
        </FormGroup>
        {/* <div className="upload-files">
          <label htmlFor="file-upload" className="upload-label">
            Anexar arquivo
          </label>
          <input id="file-upload" type="file" disabled={type === 'view'} onChange={handleFileChange} />
          {selectedFile && <span className="file-name">{selectedFile.name}</span>}
        </div> */}
      </StyledForm>
    </>
  );
};

export default JurisprudenciaForm;
